import React, { useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { MagnifyingGlass, ArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Trace() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  const search = async () => {
    try {
      const { data } = await api.get(`/trace/${q.trim()}`);
      setData(data);
    } catch (e) { toast.error("Batch not found"); setData(null); }
  };

  return (
    <div data-testid="trace-page">
      <PageHeader subtitle="End-to-End" title="Batch Traceability"/>

      <Card className="shadow-none mb-6">
        <CardContent className="p-5">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16}/>
              <Input className="pl-9" placeholder="Enter batch number e.g. CH250718001" value={q} onChange={e=>setQ(e.target.value)} data-testid="trace-input"/>
            </div>
            <Button onClick={search} disabled={!q.trim()} data-testid="trace-search">Search</Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {[
              { title: "Supplier", items: (data.materials || []).map(m => `${m.supplier_name || "Various"} — ${m.name}`) },
              { title: "Raw Material Lots", items: (data.materials || []).map(m => `${m.name} · Lot ${m.batch_no || "—"}`) },
              { title: "Production Batch", items: [`${data.batch.batch_no} · ${data.batch.sku_name}`, `Cooked: ${data.batch.cooked_weight_kg} kg → Dry: ${data.batch.dry_weight_kg} kg (Yield ${data.batch.yield_pct}%)`] },
              { title: "Freeze Dryer", items: [data.batch.machine_name || "Not yet assigned"] },
              { title: "Packaging", items: [`${data.batch.packed_qty} pouches · MRP printed`] },
              { title: "QC Results", items: (data.qc || []).map(q => `${q.stage} → ${q.result}${q.notes ? " · " + q.notes : ""}`) },
              { title: "Finished Goods", items: (data.finished_goods || []).map(f => `${f.qty} pouches @ ${f.location} · Exp ${new Date(f.exp_date).toLocaleDateString()}`) },
            ].map((step, i) => (
              <div key={i} className="relative">
                <Card className="shadow-none">
                  <CardContent className="p-5">
                    <div className="overline mb-2">Step {i+1}</div>
                    <div className="font-display font-bold text-lg mb-3">{step.title}</div>
                    <ul className="space-y-1 text-sm">
                      {step.items.length ? step.items.map((it, j) => <li key={j} className="text-muted-foreground">• {it}</li>) : <li className="text-muted-foreground italic">No data</li>}
                    </ul>
                  </CardContent>
                </Card>
                {i < 6 && <div className="flex justify-center py-1"><ArrowDown size={18} className="text-primary"/></div>}
              </div>
            ))}
          </div>
          <div>
            <Card className="shadow-none sticky top-24">
              <CardContent className="p-5">
                <div className="overline mb-3">Batch Summary</div>
                <div className="mono text-2xl font-black mb-3">{data.batch.batch_no}</div>
                <StatusBadge status={data.batch.status}/>
                <div className="flex justify-center py-6 border-t border-b border-border my-4">
                  <QRCodeCanvas value={JSON.stringify({batch:data.batch.batch_no, sku:data.batch.sku_code, mfg:data.batch.mfg_date, exp:data.batch.exp_date})} size={168} fgColor="#0A0A0A"/>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">SKU</span><span className="mono">{data.batch.sku_code}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Mfg</span><span>{data.batch.mfg_date ? new Date(data.batch.mfg_date).toLocaleDateString() : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Exp</span><span>{data.batch.exp_date ? new Date(data.batch.exp_date).toLocaleDateString() : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Yield</span><span className="mono">{data.batch.yield_pct || "—"}%</span></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
